import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AppContextType } from '@/app/providers/AppContext.types';

interface ContextStore {
  /**
   * Last context explicitly selected by user.
   * Persisted to localStorage so the user's preference survives page reload.
   *
   * NOTE: This is NOT the authoritative source of truth for the current
   * active context — that's the URL. This is only used as a fallback when
   * the URL doesn't specify a context (e.g. at '/').
   */
  lastSelectedContext: AppContextType;
  setLastSelectedContext: (ctx: AppContextType) => void;
}

export const useContextStore = create<ContextStore>()(
  persist(
    (set) => ({
      lastSelectedContext: 'home',
      setLastSelectedContext: (ctx) => set({ lastSelectedContext: ctx }),
    }),
    {
      name: 'vnp.active_context',
      storage: createJSONStorage(() => localStorage),
      version: 1,
      /**
       * Migration: reset to 'home' if stored value is invalid.
       * This handles cases where the store schema changes between versions.
       */
      migrate: (persistedState: unknown, _version: number) => {
        const validContexts: AppContextType[] = ['home', 'workplace', 'code', 'admin'];
        const state = persistedState as Partial<ContextStore>;
        if (!state?.lastSelectedContext || !validContexts.includes(state.lastSelectedContext)) {
          return { lastSelectedContext: 'home' as AppContextType };
        }
        return state as ContextStore;
      },
    }
  )
);
